/**
 * BRXCheckoutSubmitting
 *
 * Transient feedback for the final Embeddables submit step: a simulated
 * progress bar plus staged reassurance copy layered on top of the still-
 * mounted intake host, and an error banner shown above it when submission
 * fails (e.g. phone conflict). A beforeunload guard discourages navigating
 * away mid-submit.
 *
 * Usage:
 *   var ui = window.BRXCheckoutSubmitting.mount(sectionRoot);
 *   ui.showSubmitting('Submitting your information\u2026');
 *   ui.showError({ errorType: 'session_failed', message: 'Could not complete\u2026' });
 *   ui.hideSubmitting();
 *   ui.hideError();
 */
(function () {
    var PROGRESS_CAP    = 92;   // bar eases toward this and never completes on its own
    var PROGRESS_TAU_MS = 4500; // ease-out time-constant — reaches ~86% by a typical 12s wait
    var SNAP_HOLD_MS    = 150;  // how long the bar holds at 100% before the overlay hides

    var STAGE_MESSAGES = [
        { at: 4000, text: 'Confirming your details\u2026' },
        { at: 8000, text: 'Almost done\u2026' }
    ];

    function mount(root) {
        var submittingEl   = root ? root.querySelector('[data-intake-submitting]') : null;
        var messageEl      = submittingEl ? submittingEl.querySelector('[data-intake-submitting-message]') : null;
        var progressBarEl  = submittingEl ? submittingEl.querySelector('[data-intake-submitting-progress-bar]') : null;
        var errorEl        = root ? root.querySelector('[data-intake-error]') : null;
        var errorMessageEl = errorEl ? errorEl.querySelector('[data-intake-error-message]') : null;
        var restartEl      = errorEl ? errorEl.querySelector('[data-intake-restart]') : null;

        var progressRaf  = null;
        var messageTimers = [];

        if (restartEl) {
            restartEl.addEventListener('click', performRestart);
        }

        function performRestart() {
            var hostEl = root ? root.querySelector('[data-intake-host] savvy') : null;
            var flowId = hostEl ? hostEl.id : null;

            ['SavvyFormUserData', 'SavvyFormCloudUserData'].forEach(function (key) {
                try {
                    var data = JSON.parse(localStorage.getItem(key) || '{}');
                    if (flowId && Object.prototype.hasOwnProperty.call(data, flowId)) {
                        delete data[flowId];
                        if (Object.keys(data).length === 0) {
                            localStorage.removeItem(key);
                        } else {
                            localStorage.setItem(key, JSON.stringify(data));
                        }
                    }
                } catch (e) {}
            });

            try {
                var session = JSON.parse(localStorage.getItem('brx-session') || '{}');
                if (session && typeof session === 'object') {
                    session.checkout    = null;
                    session.brxFlowPage = 'savvy';
                    session.updatedAt   = Date.now();
                    localStorage.setItem('brx-session', JSON.stringify(session));
                }
            } catch (e) {}

            window.location.reload();
        }

        function onBeforeUnload(e) {
            e.preventDefault();
            e.returnValue = '';
        }

        function addUnloadGuard()    { window.addEventListener('beforeunload', onBeforeUnload); }
        function removeUnloadGuard() { window.removeEventListener('beforeunload', onBeforeUnload); }

        function clearMessageTimers() {
            for (var i = 0; i < messageTimers.length; i++) clearTimeout(messageTimers[i]);
            messageTimers = [];
        }

        function scheduleStageMessages() {
            clearMessageTimers();
            STAGE_MESSAGES.forEach(function (stage) {
                messageTimers.push(setTimeout(function () {
                    if (messageEl) messageEl.textContent = stage.text;
                }, stage.at));
            });
        }

        function setProgressWidth(pct) {
            if (progressBarEl) progressBarEl.style.width = Math.max(0, Math.min(100, pct)) + '%';
        }

        function stopProgress() {
            if (progressRaf) { cancelAnimationFrame(progressRaf); progressRaf = null; }
        }

        // Eases toward PROGRESS_CAP and never reaches it on its own — the bar
        // should never look "finished" before brx:checkout-ready actually fires.
        function startProgress() {
            stopProgress();
            setProgressWidth(0);
            var start = performance.now();

            function tick(now) {
                var elapsed = now - start;
                var pct = PROGRESS_CAP * (1 - Math.exp(-elapsed / PROGRESS_TAU_MS));
                setProgressWidth(pct);
                progressRaf = requestAnimationFrame(tick);
            }
            progressRaf = requestAnimationFrame(tick);
        }

        function showSubmitting(message) {
            hideError();
            if (messageEl && message) messageEl.textContent = message;
            if (submittingEl) {
                submittingEl.classList.remove('is-hidden');
                submittingEl.setAttribute('aria-busy', 'true');
            }
            startProgress();
            scheduleStageMessages();
            addUnloadGuard();
        }

        // Immediate hide, no completion snap — used internally on the error path.
        function hideSubmittingImmediate() {
            stopProgress();
            clearMessageTimers();
            removeUnloadGuard();
            setProgressWidth(0);
            if (submittingEl) {
                submittingEl.classList.add('is-hidden');
                submittingEl.setAttribute('aria-busy', 'false');
            }
        }

        // Public: success path. Snap the bar to 100%, hold briefly, then hide.
        function hideSubmitting() {
            stopProgress();
            clearMessageTimers();
            setProgressWidth(100);
            setTimeout(hideSubmittingImmediate, SNAP_HOLD_MS);
        }

        function showError(detail) {
            var message    = detail && typeof detail === 'object' ? detail.message : String(detail || '');
            var errorType  = detail && typeof detail === 'object' ? detail.errorType : 'unknown';
            var showRestart = errorType !== 'phone_conflict';

            hideSubmittingImmediate();
            if (errorEl) {
                if (errorMessageEl) errorMessageEl.textContent = message;
                errorEl.classList.remove('is-hidden');
            }
            if (restartEl) {
                if (showRestart) restartEl.classList.remove('is-hidden');
                else restartEl.classList.add('is-hidden');
            }
        }

        function hideError() {
            if (errorEl) errorEl.classList.add('is-hidden');
            if (restartEl) restartEl.classList.add('is-hidden');
        }

        return {
            showSubmitting: showSubmitting,
            hideSubmitting: hideSubmitting,
            showError: showError,
            hideError: hideError
        };
    }

    window.BRXCheckoutSubmitting = { mount: mount };
})();
